import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import qs from 'query-string';
import useIsMobile from 'hooks/useIsMobile';
import { useBacktrack } from 'components/common/withBacktrack';
import { useAppUrlParams, UrlParamName } from 'hooks/navigation/useAppUrlParams';

function isInDocumentOrChecklistView(pathname: string): boolean {
  return pathname.includes('/document') || pathname.includes('/checklist');
}

interface UseShowListResult {
  setObserverOn: () => void;
  showList: boolean;
  setShowList: Dispatch<SetStateAction<boolean>>;
}
export function useShowList(
  activeTemplateId: string | undefined,
  activeRef: { current: string | undefined },
  searchPhrase: string,
  loading: boolean
): UseShowListResult {
  const prevSearchPhraseRef = useRef<string | undefined>('');
  const navigate = useNavigate();
  const urlProjectId = useAppUrlParams(UrlParamName.PROJECT_ID);
  const idInRoute = useAppUrlParams(UrlParamName.INSPECTION_TEMPLATE_ID);
  const urlPrefix = useMemo(() => {
    return urlProjectId ? `/project/${urlProjectId}` : '';
  }, [urlProjectId]);
  const isMobile = useIsMobile();
  const [observing, setObserving] = useState<boolean>(false);
  const { back: goBack } = useBacktrack();

  const location = useLocation();
  const { preview } = qs.parse(location.search);

  const [showList, setShowList] = useState(
    isInDocumentOrChecklistView(location.pathname)
      ? false
      : preview !== 'true'
  );

  const setObserverOn = useCallback(() => {
    if (isMobile) {
      setObserving(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (loading) return;

    if (idInRoute && !activeRef.current) {
      if (!showList) {
        goBack();
      }
    }
  }, [
    activeTemplateId,
    activeRef,
    idInRoute,
    showList,
    goBack,
    loading,
  ]);

  useEffect(() => {
    if (!isMobile || !idInRoute) return;

    const { preview } = qs.parse(location.search);

    if (showList && preview === 'true') {
      navigate(`${urlPrefix}/inspectionTemplate`, { replace: true });
    }

    if (!showList && preview !== 'true' && idInRoute) {
      navigate(`${location.pathname}?preview=true`, { replace: true });
    }
  }, [
    showList,
    isMobile,
    idInRoute,
    location.search,
    location.pathname,
    navigate,
    urlPrefix,
  ]);

  useEffect(() => {
    if (!observing || !activeRef.current || !isMobile || loading) {
      return;
    }

    if (isInDocumentOrChecklistView(location.pathname)) {
      return;
    }

    navigate(
      `${urlPrefix}/inspectionTemplate/${activeRef.current}?preview=${!showList}`
    );
    setObserving(false);
  }, [
    observing,
    navigate,
    activeRef,
    isMobile,
    showList,
    urlPrefix,
    loading,
    location.pathname,
  ]);

  useEffect(() => {
    const preview = prevSearchPhraseRef.current === searchPhrase;

    if (!preview) {
      setShowList(true);
      prevSearchPhraseRef.current = searchPhrase;
    }
  }, [searchPhrase]);

  useEffect(() => {
    const { preview } = qs.parse(location.search);

    if (isInDocumentOrChecklistView(location.pathname)) {
      return;
    }

    if (isMobile && !showList && preview !== 'true' && idInRoute) {
      navigate(`${location.pathname}?preview=true`, { replace: true });
      return;
    }

    setShowList(preview !== 'true');
  }, [location.search, location.pathname, isMobile, showList, idInRoute, navigate]);

  return { setObserverOn, showList, setShowList };
}
